import type { DocOptionSet } from "building-types/options/optionSet";
import { isBooleanOptionSet, optionSetOptionIds } from "./optionSetBaseUtils";

enum OptionSetValidationErrorType {
  OUT_OF_RANGE,
  DOES_NOT_EXIST,
  NOT_A_NUMBER,
  WRONG_TYPE,
  INVALID_OPTION,
  INVALID_OPTION_SET,
  DISABLED_OPTION,
}

interface OptionSetValidationError {
  type: OptionSetValidationErrorType;
  message: string;
}

/**
 * Looks up a DocOption by ID on a DocOptionSet, and verifies that it is present,
 * of the correct form, and enabled.
 *
 * @param {DocOptionSet} optionSet
 * @param {string} optionId
 *
 * @returns {OptionSetValidationError} An error describing the validation issue, if any are present
 */
export function validateOptionSet(
  optionSet: DocOptionSet,
  optionId: string | null | undefined,
): OptionSetValidationError | null {
  if (optionId === null || optionId === undefined) {
    return {
      type: OptionSetValidationErrorType.INVALID_OPTION,
      message: "No optionId argument provided.",
    };
  }

  if (optionSet?.options?.[optionId]?.disabled) {
    return {
      type: OptionSetValidationErrorType.DISABLED_OPTION,
      message: `The option "${optionSet.name}" is disabled.`,
    };
  }

  if (isBooleanOptionSet(optionSet)) {
    if (optionId !== "true" && optionId !== "false") {
      return {
        type: OptionSetValidationErrorType.WRONG_TYPE,
        message: `The optionId must be "true" or "false" for boolean option sets.`,
      };
    }

    return null;
  } else {
    const values = optionSetOptionIds(optionSet);

    if (
      !values ||
      values.length === 0 ||
      typeof values[0] !== typeof optionId ||
      (values as any).indexOf(optionId) === -1
    ) {
      return {
        type: OptionSetValidationErrorType.INVALID_OPTION,
        message: `The option '${optionId}' is not a valid enum value.`,
      };
    }

    return null;
  }
}
